import { DLabDataGrid, FileSelectIcon, THEME } from "@digitallab/grid-common-components";
import { OwcTypography, OwcButton, OwcIcon } from "@one/react";
import { APP_PATHS, equipmentStatus } from "../constants";
import { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { LogsTopBar } from "./LogsTopBar";
import { Stepper } from "./Stepper";
import ActionDataInputsBox from "../features/log-book/log-sheet/log-sheet-form/ActionDataInputsBox";
import { COLUMNS_DEFINITIONS } from "./columnDefinitions";
import { useFormikContext } from "formik";
import RunDataInputsBox from "../features/log-book/run-logs/run-logs-form/RunDataInputsBox";
import {
  CloudSearchEquipmentModelType,
  DigitalLabLogbookInstrumentModelType
} from "../models/DigitalLabLogbookInstrumentModelType";
import { SetEquipmentsForLogBookDialog } from "../components/shared/SetEquipmentsForLogBook/SetEquipmentsForLogBookDialog";
import { useSelector } from "react-redux";
import { find } from "lodash";
import { IActionLogFormValues, InstrumentDetailsForStepperModelType, KEEP_STATUS_VALUE } from "./StepperContainer";
import { WithApolloClient } from "react-apollo";
import { loadEquipmentsData } from "./loadEquipmentData";
import { RootState } from "../reducers";
import { DialogServiceContext } from "../utils/hooks/dialogService/DialogServiceContext";
import { react18WorkaroundProps } from "../utils/constants/react18WorkaroundProps";
import { AdjustedIDialogProps } from "../utils/hooks/dialogService/useDialogService";

const FOUR_EP_SITES = process.env.REACT_APP_4EP_SITES || [""];
const ADD_EQUIPMENT_TEXT = "Add equipment from the list";
const initialSteps = [
  {
    mainText: "Select equipment",
    optionalText: "xxx",
    tooltipText: ""
  },
  {
    mainText: "Define run",
    optionalText: "Define the details of the run log",
    tooltipText: ""
  }
];

export const StepperForm = ({
  equipmentList: selectedEquipmentList = [],
  setEquipmentListWithDetails: setSelectedEquipmentList,
  client,
  landingPageAppSyncClient
}: {
  equipmentList: InstrumentDetailsForStepperModelType[];
  setEquipmentListWithDetails: React.Dispatch<React.SetStateAction<InstrumentDetailsForStepperModelType[] | undefined>>;
  client: WithApolloClient<any>;
  landingPageAppSyncClient: WithApolloClient<any>;
}) => {
  const gxpReadys = useSelector((store: RootState) => store.runLogsForm.gxpReadys);
  const systemStatuses = useSelector((store: RootState) => store.runLogsForm.systemStatuses);

  const { pathname } = useLocation();
  const isRunLog = pathname.includes(APP_PATHS.RUN_LOG);
  const isActionLog = pathname.includes(APP_PATHS.ACTION_LOG);
  const formik = useFormikContext();
  const { submitForm } = formik;
  const history = useHistory();

  const [steps, setSteps] = useState(initialSteps);
  if (!isRunLog)
    steps[1] = {
      mainText: "Define action",
      optionalText: "Define the details of the action log",
      tooltipText: ""
    };
  const [presentStep, setPresentStep] = useState(0);
  const dialogService = useContext(DialogServiceContext);
  const [disableNextStep, setDisableNextStep] = useState(false);
  const [isPropagationDisabled, setIsPropagationDisabled] = useState(true);

  const [openModal, setOpenModal] = useState(false);

  const attributes = {
    height: "55vh",
    rowQuickSearch: false,
    suppressPaginationPanel: true,
    noRowsOverlayComponent: () => (
      <div style={{ textAlign: "center", pointerEvents: "auto" }}>
        <OwcIcon {...react18WorkaroundProps}>
          <FileSelectIcon />
        </OwcIcon>
        <OwcTypography {...react18WorkaroundProps} variant="body2">
          Selected equipment will appear here
        </OwcTypography>
        <OwcButton {...react18WorkaroundProps} style={{ display: "inline-block" }} onClick={() => setOpenModal(true)}>
          {ADD_EQUIPMENT_TEXT}
        </OwcButton>
      </div>
    ),
    pagination: false,
    animateRows: true,
    rowMultiSelectWithClick: true,
    rowExport: false,
    masterDetail: true,
    hiddenByDefault: true,
    serverSideInfiniteScroll: false,
    suppressRowClickSelection: true,
    paginationConfig: { rowsPerPage: 0, total: 0 },
    disablePagination: true,
    defaultColDef: {
      flex: 1,
      minWidth: 90,
      suppressMenu: true,
      sortable: true
    },
    onGridReady: () => { }
  };

  useEffect(() => {
    if (selectedEquipmentList.length === 0) {
      setDisableNextStep(true);
      steps[0].optionalText = "No equipment selected";
      steps[0].tooltipText = "Please select at least one equipment to proceed";
      setSteps([...steps]);
    } else {
      steps[0].optionalText = `${selectedEquipmentList[0].equipmentModel}${selectedEquipmentList.length > 1 ? " +" + (selectedEquipmentList.length - 1) : ""
        }`;

      const formatEquipmentValue = (val: any) => (val == null || String(val).toLowerCase() === "null" ? "-" : val);
      steps[0].tooltipText = selectedEquipmentList
        .map((item) =>
          [item.equipmentModel, item.equipmentId, item.serialNumber, item.equipmentNickName]
            .map(formatEquipmentValue)
            .join(", ")
        )
        .join("\n");
      setSteps([...steps]);
      setDisableNextStep(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEquipmentList.length]);

  useEffect(() => {
    if (presentStep === 1 && !isRunLog) {
      if (selectedEquipmentList?.length === 1) {
        if (selectedEquipmentList[0].qualificationStatus) {
          const qualificationStatus = find(gxpReadys, {
            key: selectedEquipmentList[0].qualificationStatus
          }) ||
            find(gxpReadys, {
              value: selectedEquipmentList[0].qualificationStatus
            }) || {
            key: "",
            value: ""
          };
          formik.setFieldValue("gxpReady", { newValue: qualificationStatus, shouldBePublished: false });
        }
        if (selectedEquipmentList[0].systemStatus) {
          const systemStatus =
            find(systemStatuses, {
              value: selectedEquipmentList[0].systemStatus
            }) ||
            find(systemStatuses, {
              key: selectedEquipmentList[0].systemStatus
            });

          formik.setFieldValue("systemStatus", { newValue: systemStatus, shouldBePublished: false });
        }
        if (selectedEquipmentList[0].configurationDetails) {
          formik.setFieldValue("logbookConfigurationDetails", {
            newValue: selectedEquipmentList[0].configurationDetails,
            shouldBePublished: false
          });
        }
      } else {
        formik.setFieldValue("gxpReady", { newValue: KEEP_STATUS_VALUE, shouldBePublished: false });
        formik.setFieldValue("systemStatus", { newValue: KEEP_STATUS_VALUE, shouldBePublished: false });
        formik.setFieldValue("logbookConfigurationDetails", { newValue: "", shouldBePublished: false }, true);
        formik.setFieldValue("logbookConfigurationBaseline", { newValue: "", shouldBePublished: false }, true);
        formik.setFieldValue("updatedSoftwareVersion", { newValue: "", shouldBePublished: false }, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentStep, isPropagationDisabled, selectedEquipmentList.length]);

  useEffect(() => {
    const checkPropagationDisabled = () =>
      selectedEquipmentList.some((item) => {
        const site = FOUR_EP_SITES.includes(item.siteName);
        const group = item.belongingToGroup?.startsWith("DSR");
        const status = item.status === equipmentStatus.pending?.key;
        return (site && group) || status;
      });
    const disablePropagation = checkPropagationDisabled();
    setIsPropagationDisabled(disablePropagation);
  }, [selectedEquipmentList]);

  const handleCancel = () =>
    (
      dialogService.show({
        title: `Cancel Add ${isRunLog ? "Run" : "Action"} log(s)`,
        confirmText: "Cancel",
        cancelText: "Keep editing",
        body: "By canceling you will loose all the changes you have made."
      }) as Promise<unknown>
    )
      .then(() => {
        const currentOrigin = window.location.origin;
        sessionStorage.setItem("equipmentIdList", JSON.stringify([]));
        if (document.referrer && document.referrer.startsWith(currentOrigin)) {
          history.goBack();
        } else {
          history.push(APP_PATHS.ROOT);
        }
      })
      .catch(() => { });

  const handleDelete = ({ data }: { data: DigitalLabLogbookInstrumentModelType }) => {
    const deleted = selectedEquipmentList.filter(
      (item) =>
        !(
          item.equipmentId === data.equipmentId &&
          (!data.serialNumber || item.serialNumber === data.serialNumber) &&
          item.equipmentModel === data.equipmentModel
        )
    );
    setSelectedEquipmentList(deleted);
    sessionStorage.setItem(
      "equipmentIdList",
      JSON.stringify(deleted.map(({ inventoryId }) => inventoryId))
    );
  };

  const setStep = (step: number) => {
    if (presentStep === 0 && isActionLog) {
      if (selectedEquipmentList.length > 1 && !selectedEquipmentList.every((item) => item.systemStatus)) {
        dialogService.show({
          title: <p style={{ margin: 0, textAlign: "center", width: "100%" }}>Attention</p>,
          confirmText: "OK",
          confirmOnly: true,
          centeredButtons: true,
          body: <div>
            <p style={{ marginTop: "-1rem" }}>
              One or more of the selected equipment does not have a System Status set in Repository.
            </p>
            <p>
              Reach out to your local Repository administrator for help and/or remove the respective equipment to continue with the log entry.
            </p>
          </div>,
          size: "sm",
        } as AdjustedIDialogProps);
        return;
      }
    }
    setPresentStep(step);
  };

  useEffect(() => {
    formik.setFieldValue("equipmentListWithDetails", selectedEquipmentList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEquipmentList]);

  return (
    <>
      <LogsTopBar titleText={`Add ${isRunLog ? "Run" : "Action"} log(s)`} onCancel={handleCancel} />
      <Stepper
        onCancel={handleCancel}
        presentStep={presentStep}
        steps={steps}
        nextStepDisabled={disableNextStep}
        setStep={setStep}
        finishText={`Add ${isRunLog ? "Run" : "Action"} log(s)`}
        finishDisabled={!formik.isValid}
        stepperStyle={{ width: "35%", pointerEvents: "inherit" }}
        isSubmitting={formik.isSubmitting}
        handleFinish={async () => {
          const formikValues = formik.values as IActionLogFormValues;
          if (
            !isRunLog &&
            !isPropagationDisabled &&
            (formikValues.systemStatus?.shouldBePublished ||
              formikValues.gxpReady?.shouldBePublished ||
              formikValues.updatedSoftwareVersion?.shouldBePublished ||
              formikValues.logbookConfigurationDetails?.shouldBePublished ||
              formikValues.logbookConfigurationBaseline?.shouldBePublished)
          ) {
            const valuesToPropagate = [];
            if (formikValues.systemStatus?.shouldBePublished) valuesToPropagate.push("System status");
            if (formikValues.gxpReady?.shouldBePublished) valuesToPropagate.push("Qualification status");
            if (formikValues.updatedSoftwareVersion?.shouldBePublished) valuesToPropagate.push("Software version");
            if (formikValues.logbookConfigurationBaseline?.shouldBePublished) valuesToPropagate.push("Configuration baseline");
            if (formikValues.logbookConfigurationDetails?.shouldBePublished)
              valuesToPropagate.push("Configuration details");
            (
              dialogService.show({
                title: "Propagate to repository",
                confirmText: "Proceed",
                body: `${valuesToPropagate
                  .join(", ")
                  .replace(/,([^,]*)$/, " and$1")} will be propagated to repository. Do you want to proceed?`
              }) as Promise<unknown>
            )
              .then(() => {
                submitForm();
              })
              .catch(() => { });
          } else await submitForm();
        }}
      />
      <div
        id="Form body"
        style={{
          width: "100%",
          backgroundColor: THEME["one-color-cobas-accent-gray-100"],
          padding: THEME["one-spacer-16"],
          paddingBottom: THEME["one-spacer-68"]
        }}
      >
        {presentStep === 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div style={{ marginBottom: THEME["one-spacer-16"] }}>
              <OwcTypography {...react18WorkaroundProps} variant="title5">
                Selected equipment
              </OwcTypography>
              <OwcTypography {...react18WorkaroundProps}>
                Add the equipment for which you want to define an action
              </OwcTypography>
            </div>

            <div style={{ padding: THEME["one-spacer-16"] }}>
              <DLabDataGrid
                rowData={selectedEquipmentList.map((item) => ({ ...item, id: item.inventoryId }))}
                columnDefs={COLUMNS_DEFINITIONS(handleDelete)}
                {...attributes}
              />
            </div>
            {selectedEquipmentList.length !== 0 && (
              <OwcButton
                {...react18WorkaroundProps}
                style={{
                  width: 400,
                  marginTop: 32,
                  alignSelf: "flex-end"
                }}
                onClick={() => setOpenModal(true)}
                variant="secondary"
              >
                {ADD_EQUIPMENT_TEXT}
              </OwcButton>
            )}
            <SetEquipmentsForLogBookDialog
              cancelTitle={"Cancel"}
              columnDefs={COLUMNS_DEFINITIONS(handleDelete)}
              initiallySelectedEquipments={selectedEquipmentList}
              key={JSON.stringify(selectedEquipmentList)}
              approveTitle={"Add"}
              open={openModal}
              close={() => setOpenModal(false)}
              title={ADD_EQUIPMENT_TEXT}
              landingPageAppSyncClient={landingPageAppSyncClient}
              onDialogApproveCallBack={async (
                data: {
                  id: string;
                  rowData: CloudSearchEquipmentModelType;
                }[]
              ) => {
                const addedEquipment = await loadEquipmentsData(
                  data.map((x) => x.id),
                  client,
                  history,
                  pathname
                );
                setOpenModal(false);
                sessionStorage.setItem(
                  "equipmentIdList",
                  JSON.stringify(
                    [...selectedEquipmentList, ...addedEquipment]
                      .map(({ inventoryId }) => inventoryId)
                  )
                );
                setSelectedEquipmentList([...selectedEquipmentList, ...addedEquipment]);
              }}
            />
          </div>
        )}
        {presentStep === 1 && (
          <div style={{ marginBottom: THEME["one-spacer-16"] }}>
            <OwcTypography
              {...react18WorkaroundProps}
              style={{
                marginBottom: "1rem",
                fontWeight: "bold"
              }}
              variant="title6"
            >
              Defined {isRunLog ? "run" : "action"}
            </OwcTypography>
            {isRunLog ? (
              <RunDataInputsBox equipmentDetail={{}} client={client} />
            ) : (
              <ActionDataInputsBox
                defaultValues={selectedEquipmentList[0]}
                numberOfSelectedEquipment={selectedEquipmentList.length}
                isPropagationDisabled={isPropagationDisabled}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};
